// src/views/Representatives/ManageBursaries.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Alert,
  Spinner,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import instance from '../../api/Axios';
import usePermissions from '../../hooks/usePermissions';

const ManageBursaries = () => {
  const [bursaries, setBursaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const { canCreateBursaries, canViewBursaries, canManageBursaries, loading: permissionsLoading } = usePermissions();

  const fetchBursaries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      console.log('=== FETCHING BURSARIES ===');
      console.log('Timestamp:', new Date().toISOString());
      console.log('Access token:', localStorage.getItem('access_token') ? 'Present' : 'Missing');
      
      const response = await instance.get('/api/bursaries/');
      console.log('=== BURSARIES API RESPONSE ===');
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Is response.data an array?', Array.isArray(response.data));
      console.log('Response data length:', Array.isArray(response.data) ? response.data.length : 'N/A');
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        console.log('Found bursaries:', response.data.map(b => ({ id: b.id, name: b.name })));
      }
      
      console.log('Setting bursaries state to:', response.data);
      setBursaries(response.data);
    } catch (error) {
      console.error('Error fetching bursaries:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        setError('You do not have permission to view bursaries.');
      } else {
        setError('Failed to load bursaries. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('=== BURSARIES USEEFFECT TRIGGERED ===');
    console.log('Permissions loading:', permissionsLoading);
    
    // Don't fetch if permissions are still loading
    if (permissionsLoading) {
      console.log('Permissions still loading, waiting...');
      return;
    }
    
    // Check permissions inside the effect to avoid dependency issues
    const hasViewPermission = canViewBursaries();
    console.log('Can view bursaries:', hasViewPermission);
    
    if (hasViewPermission) {
      console.log('Permissions loaded and user can view bursaries, fetching...');
      fetchBursaries();
    } else {
      console.log('User does not have bursary view permissions');
      setError('You do not have permission to view bursaries.');
      setLoading(false);
    }
  }, [permissionsLoading]); // Only depend on permissionsLoading to avoid infinite loops

  const handleViewBursary = (bursary) => {
    console.log('Viewing bursary:', bursary.name);
    navigate(`/general-dashboard/bursaries/${bursary.id}`);
  };

  const handleEditBursary = (bursary) => {
    console.log('Editing bursary:', bursary.name);
    navigate(`/general-dashboard/bursaries/edit/${bursary.id}`);
  };

  const handleDeleteBursary = async (bursary) => {
    if (window.confirm(`Are you sure you want to delete the bursary "${bursary.name}"?`)) {
      try {
        await instance.delete(`/api/bursaries/${bursary.id}/`);
        console.log('Bursary deleted successfully');
        fetchBursaries(); // Refresh the list
      } catch (error) {
        console.error('Error deleting bursary:', error);
        setError('Failed to delete bursary. Please try again.');
      }
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatter.format(amount);
  };

  if (!canViewBursaries()) {
    return (
      <div>
        <Alert color="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          You do not have permission to view bursaries.
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading Bursaries...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Bursaries</h2>
        <div className="d-flex gap-2">
          {canCreateBursaries() && (
            <Button 
              color="primary" 
              onClick={() => navigate('/general-dashboard/CreateBursary')}
              className="d-flex align-items-center"
            >
              <i className="fas fa-plus me-2"></i>
              ADD +
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardBody>
          <CardTitle tag="h5">
            <i className="fas fa-graduation-cap me-2"></i>
            Bursary Management
          </CardTitle>
          
          {error && (
            <Alert color="danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {(() => {
            console.log('Rendering bursaries. Current state:', bursaries);
            console.log('Bursaries length:', bursaries.length);
            console.log('Bursaries type:', typeof bursaries);
            console.log('Is bursaries an array?', Array.isArray(bursaries));
            
            return bursaries.length === 0 ? (
              <Alert color="info">
                <i className="fas fa-info-circle me-2"></i>
                No bursaries found. {canCreateBursaries() && 'Click "ADD +" to create your first bursary.'}
                <br />
                <small>Debug: bursaries state = {JSON.stringify(bursaries)}</small>
              </Alert>
            ) : (
            <div className="table-responsive" style={{ minHeight: '500px' }}>
              <Table striped hover style={{ minHeight: '450px' }}>
                <thead>
                  <tr>
                    <th>Bursary Code</th>
                    <th>Bursary Name</th>
                    <th>Funder</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Currency</th>
                    <th>Funding Amount</th>
                    <th>Beneficiaries</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bursaries.map((bursary) => (
                    <tr key={bursary.id}>
                      <td>
                        <strong className="text-primary">{bursary.bursary_code || '-'}</strong>
                      </td>
                      <td>
                        <strong>{bursary.name}</strong>
                      </td>
                      <td>
                        {bursary.funder_name || '-'}
                      </td>
                      <td>
                        {formatDate(bursary.start_date)}
                      </td>
                      <td>
                        {formatDate(bursary.end_date)}
                      </td>
                      <td>
                        <Badge color="info" pill>
                          {bursary.bursary_currency || 'ZAR'}
                        </Badge>
                      </td>
                      <td>
                        {formatCurrency(bursary.funding_amount, bursary.bursary_currency)}
                      </td>
                      <td>
                        <Badge color="success" pill>
                          {bursary.number_of_beneficiaries || 0}
                        </Badge>
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle 
                            size="sm" 
                            color="outline-primary"
                          >
                            ⋯
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem 
                              onClick={() => handleViewBursary(bursary)}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View
                            </DropdownItem>
                            {canManageBursaries() && (
                              <DropdownItem 
                                onClick={() => handleEditBursary(bursary)}
                              >
                                <i className="fas fa-edit me-2"></i>
                                Edit
                              </DropdownItem>
                            )}
                            {canManageBursaries() && (
                              <DropdownItem 
                                onClick={() => handleDeleteBursary(bursary)}
                                className="text-danger"
                              >
                                <i className="fas fa-trash me-2"></i>
                                Delete
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          );
          })()}
        </CardBody>
      </Card>


    </div>
  );
};

export default ManageBursaries;